import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Route as ToolsIndexRoute } from 'src/routes/tools.index';
import { useNavigate } from '@tanstack/react-router';
import LoadingDots from 'src/modules/shared/components/atoms/dots';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { useTenantAutomations } from 'src/modules/shared/hooks/api/automations/workflows/automations';
import { useState } from 'react';
import {
  PiCirclesThreeLight,
  PiGear,
  PiMagnifyingGlass,
  PiPlus,
} from 'react-icons/pi';
import { useAlert } from 'src/contexts/alert-context';
import { useDeleteWorkflowTriggerMutation } from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import { CreateWorkflowTriggerModal } from '../../molecules/create-trigger-modal';

export const AutomationsPage: React.FC = () => {
  const navigate = useNavigate();
  // Listing uses DRAFT by default; version selection happens inside the modal
  const listingVersion = 'DRAFT';
  const [deactivatingKey, setDeactivatingKey] = useState<string | null>(null);

  const {
    automations,
    loading: automLoading,
    error: automError,
  } = useTenantAutomations(listingVersion, 50, true);

  const { showAlert } = useAlert();
  const deleteWorkflowTrigger = useDeleteWorkflowTriggerMutation();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader
          className="px-4 py-3 border-b"
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                appearance="soft"
                size="md"
                onClick={() =>
                  navigate({
                    to: ToolsIndexRoute.to,
                    search: { phoneVersion: 'v1' },
                  })
                }
              >
                {/* TODO: check when implementing i18n */}
                <div className="flex items-center gap-2">
                  <PiGear className="w-4 h-4" />
                  <Text size="small">Herramientas</Text>
                </div>
              </Button>

              <Button
                variant="primary"
                size="md"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <div className="flex items-center justify-center gap-2">
                  <PiPlus className="w-4 h-4" />
                  {/* TODO: check when implementing i18n */}
                  <Text size="small">Nueva automatización</Text>
                </div>
              </Button>
            </div>
          }
        >
          <div className="flex items-center gap-4">
            <PiCirclesThreeLight className="w-8 h-8" />

            <Text as="h2" size="h2" weight="medium">
              {/* TODO: check when implementing i18n */}
              Automatizaciones
            </Text>
          </div>
        </PanelHeader>

        <div className="flex flex-col gap-4 flex-1 overflow-auto p-4">
          <div className="space-y-4">
            {automError ? (
              <div className="p-4 text-red-600">
                {/* TODO: check when implementing i18n */}
                <Text size="small">Error cargando automatizaciones</Text>
              </div>
            ) : automLoading ? (
              <div className="space-y-3">
                <CardSkeleton
                  header={{ title: true, subtitle: true, action: true }}
                  content={{ lines: 2, widths: ['w-3/4', 'w-2/3'] }}
                />
                <CardSkeleton
                  header={{ title: true, subtitle: true, action: true }}
                  content={{ lines: 2, widths: ['w-4/5', 'w-3/5'] }}
                />
              </div>
            ) : automations.length === 0 ? (
              <EmptyState
                icon={<PiMagnifyingGlass className="w-8 h-8" />}
                // TODO: check when implementing i18n
                title="No hay automatizaciones activas"
                // TODO: check when implementing i18n
                description="Crea una automatización para empezar."
                appearance="soft"
                variant="neutral"
                className="mt-4"
              />
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {automations.map((a) => {
                  const itemKey = `${a.workflowId}:${a.version}:${String(
                    a.trigger.triggerId
                  )}`;
                  const isDeactivating =
                    deleteWorkflowTrigger.isPending &&
                    deactivatingKey === itemKey;

                  return (
                    <div
                      key={`${a.workflowId}:${String(a.trigger.triggerId)}`}
                      className="border rounded-md p-3 bg-white flex items-center justify-between"
                    >
                      <div className="flex flex-col gap-1">
                        <Text size="small" weight="medium">
                          {a.workflowName || a.workflowId}
                        </Text>
                        <Text size="tiny" className="text-gray-600">
                          {String(a.trigger.triggerId)}
                        </Text>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={isDeactivating}
                          onClick={() => {
                            setDeactivatingKey(itemKey);
                            deleteWorkflowTrigger.mutate(
                              {
                                workflowId: a.workflowId,
                                version: a.version,
                                triggerId: String(a.trigger.triggerId),
                              },
                              {
                                onSuccess: () => {
                                  showAlert(
                                    'Automatización desactivada', // TODO: check when implementing i18n
                                    'success'
                                  );
                                },
                                onError: () => {
                                  showAlert(
                                    'Error desactivando la automatización', // TODO: check when implementing i18n
                                    'error'
                                  );
                                },
                                onSettled: () => setDeactivatingKey(null),
                              }
                            );
                          }}
                        >
                          <div className="min-w-[7rem] flex items-center justify-center gap-2">
                            {isDeactivating ? (
                              <>
                                {/* TODO: check when implementing i18n */}
                                <Text size="tiny">Desactivando</Text>
                                <LoadingDots size="2xs" variant="neutral" />
                              </>
                            ) : (
                              // TODO: check when implementing i18n
                              <Text size="tiny">Desactivar</Text>
                            )}
                          </div>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Panel>

      {/* Create Trigger Modal */}
      <CreateWorkflowTriggerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={() => {
          showAlert('Automatización creada', 'success');
        }}
      />
    </div>
  );
};
